import { faPencil, faPlusCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Dialog, Grid, TextField } from "@mui/material";
import { isEqual } from "lodash";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { Props, State } from "./interfaces";
import { ActionButton, DialogContent } from "./styles";
import ConfirmationChangesDialog from "../../../../../containers/ConfirmationChangesDialog/ConfirmationChangesDialog";
import { useAppSelector } from "../../../../../custom-hooks/redux/redux";
import { PropsFilter } from "../../../../../models/api/props-filter";
import { DonationUsageRef } from "../../../../../models/donation-usages";
import { ExchangeRatesFilter } from "../../../../../models/exchange-rates";
import AutocompleteDropdown from "../../../../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";
import { TextFieldTypeAmount } from "../../../../../reusable-components/Table/AddEditItem/TextFieldTypeAmount/TextFieldTypeAmount";
import { CurrenciesApiService } from "../../../../../services/currencies/currencies-api.service";
import { ExchangeRatesApiService } from "../../../../../services/exchange-rates";
import { LegalEntityProjectsApiService } from "../../../../../services/legal-entity-projects/legal-entity-projects-api.service";
import DialogActions from "../../../../DialogActions/DialogActions";
import DialogTitle from "../../../../DialogTitle/DialogTitle";
import ExchangeRateBookingsSuggestions from "../../ExchangeRateBookingsSuggestions/ExchangeRateBookingsSuggestions";

const AddEditUsagesOfDonationFundsRow = ({ handleChangeDonation, legalEntity, item, currency, date }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.add_transaction.transaction_screen_two.usages_of_donation_funds"; // tRP - translation root path

  const currCompanyAccount = useAppSelector((state) => state.currentUserReducer.currentCompanyAccount);

  const DEFAULT_ITEM: Partial<DonationUsageRef> = useMemo(() => {
    return {
      legalEntity: item?.legalEntity ?? legalEntity ?? null,
      currency: item?.currency ?? currency ?? null,
      bookedCurrency: item?.bookedCurrency ?? currency ?? null,
      project: item?.project ?? null,
      transaction: item?.transaction ?? null,
      amount: item?.amount ?? 0,
      exchangeRate: item?.exchangeRate ?? 1,
      bookedAmount: item?.bookedAmount ?? 0,
    };
  }, [legalEntity, item]);

  const INITIAL_STATE: State = useMemo(() => {
    return {
      isDialogOpen: false,
      donation: item ?? (DEFAULT_ITEM as DonationUsageRef),
      exchangeRateSuggestions: [],
      isCCDOpen: false,
      areFieldsValidated: !!item,
    };
  }, [DEFAULT_ITEM, item]);

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });

  const handleChangeDonationField = useCallback(
    (field: keyof DonationUsageRef, value: DonationUsageRef[keyof DonationUsageRef]) => {
      if (field === "bookedAmount") {
        const exchangeRate =
          !value || !state.donation.amount ? "0" : ((value as number) / state.donation.amount).toFixed(4);

        setState((state) => ({
          ...state,
          donation: { ...state.donation, [field]: value as number, exchangeRate: parseFloat(exchangeRate) },
        }));
      } else if (field === "amount" || field === "exchangeRate") {
        const bookedAmount = !value
          ? "0"
          : field === "amount"
          ? ((value as number) * state.donation.exchangeRate).toFixed(2)
          : ((value as number) * state.donation.amount).toFixed(2);

        setState((state) => ({
          ...state,
          donation: { ...state.donation, [field]: value, bookedAmount: parseFloat(bookedAmount) },
        }));
      } else {
        setState((state) => ({
          ...state,
          donation: { ...state.donation, [field]: value },
        }));
      }
    },
    [state.donation]
  );

  const handleConfirm = useCallback(async () => {
    handleChangeDonation({
      ...state.donation,
      id: item ? item.id : uuidv4(),
      companyAccount: item ? item.companyAccount : currCompanyAccount,
    });

    setState({ ...INITIAL_STATE });
  }, [INITIAL_STATE, item, handleChangeDonation, state.donation, currCompanyAccount]);

  const handleDiscardChanges = useCallback(() => {
    setState({ ...INITIAL_STATE });
  }, [INITIAL_STATE]);

  const resetAndCloseDialog = (): void => {
    if (!isEqual(state.donation, INITIAL_STATE.donation)) {
      setState((state) => ({
        ...state,
        isCCDOpen: true,
      }));
    } else {
      handleDiscardChanges();
    }
  };

  useEffect(() => {
    !item &&
      setState((state) => ({
        ...state,
        donation: {
          ...state.donation,
          legalEntity: legalEntity ?? null,
          currency: currency ?? null,
          bookedCurrency: currency ?? null,
        },
      }));
  }, [item, legalEntity, currency]);

  useEffect(() => {
    !!item &&
      setState((state) => ({
        ...state,
        donation: item,
      }));

    return () => {
      if (!item) setState({ ...INITIAL_STATE });
    };
  }, [item, INITIAL_STATE]);

  const loadExchangeRates = useCallback(
    async (signal?: AbortSignal) => {
      const apiService = new ExchangeRatesApiService();
      apiService.useLoadItemsPath("/GetSuggestedExchangeRate");

      try {
        const response = await apiService.loadItems(
          {
            sourceId: state.donation?.currency?.id,
            destinationId: state.donation?.bookedCurrency?.id,
            year: new Date(date).getFullYear(),
            month: new Date(date).getMonth() + 1,
            legalEntityId: state.donation?.legalEntity?.id,
          } as ExchangeRatesFilter,
          undefined,
          undefined,
          undefined,
          undefined,
          60,
          signal
        );

        if (response.error || signal?.aborted || !signal) {
          setState((state) => ({
            ...state,
            error: response.error,
          }));
        } else {
          setState((state) => ({
            ...state,
            donation: {
              ...state.donation,
              exchangeRate: response.items[0]?.rate ?? 1,
              bookedAmount:
                response.items[0]?.rate && !!state.donation.amount
                  ? parseFloat((response.items[0].rate * state.donation.amount).toFixed(2))
                  : state.donation.bookedAmount,
            },
            exchangeRateSuggestions: response.items,
          }));
        }
      } catch (error) {
        setState((state) => ({
          ...state,
          error: error as string,
        }));
      }
    },
    [date, state.donation?.bookedCurrency, state.donation?.currency, state.donation?.legalEntity]
  );

  useEffect(() => {
    const abortController = new AbortController();

    state.isDialogOpen &&
      !!state.donation?.legalEntity &&
      !!date &&
      !!state.donation?.bookedCurrency &&
      !!state.donation?.currency &&
      loadExchangeRates(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [date, state.donation?.bookedCurrency, state.donation?.currency, state.donation?.legalEntity, state.isDialogOpen]);

  const areFieldsValidated = useCallback((): boolean => {
    return (
      !!state.donation.project &&
      !!state.donation.amount &&
      !!state.donation.bookedAmount &&
      !!state.donation.currency &&
      !!state.donation.bookedCurrency &&
      !!state.donation.exchangeRate
    );
  }, [state.donation]);

  useEffect(() => {
    setState((state) => ({
      ...state,
      areFieldsValidated: areFieldsValidated(),
    }));
  }, [state.donation, areFieldsValidated]);

  return (
    <>
      {item ? (
        <ActionButton
          onClick={() => {
            setState((state) => ({ ...state, isDialogOpen: true }));
          }}
        >
          <FontAwesomeIcon icon={faPencil} />
        </ActionButton>
      ) : (
        <Button
          onClick={() => {
            setState((state) => ({ ...state, isDialogOpen: true }));
          }}
        >
          <FontAwesomeIcon
            icon={faPlusCircle}
            style={{ width: "25px", height: "25px" }}
          />
        </Button>
      )}
      <Dialog
        open={state.isDialogOpen}
        onClose={resetAndCloseDialog}
        fullWidth
      >
        <DialogTitle
          title={item ? t(`${tRP}.edit_title`) : t(`${tRP}.title`)}
          handleClose={resetAndCloseDialog}
        />
        <DialogContent>
          <AutocompleteDropdown
            labelKey={`${t(`${tRP}.fields.project`)}`}
            propsItem={state.donation}
            field="project"
            serviceApi={new LegalEntityProjectsApiService()}
            handleChange={handleChangeDonationField}
            propsFilter={
              {
                legalEntityIds: legalEntity?.id ? [legalEntity?.id] : [],
              } as PropsFilter
            }
            required
          />

          <Grid
            sx={{
              width: "45%",
              display: "flex",
              flexDirection: "column",
              gap: "10px",

              ".MuiAutocomplete-root, & > .MuiFormControl-root": {
                width: "100%%",
              },
            }}
          >
            <TextFieldTypeAmount
              field="amount"
              labelKey={`${t(`${tRP}.fields.amount`)}`}
              handleChange={(_, number) => handleChangeDonationField("amount", number)}
              amountValue={state.donation.amount ?? 0}
              sxProps={{
                "& .MuiCircularProgress-root": {
                  position: "absolute",
                  marginLeft: "69px",
                },
              }}
              endAdornment={
                <AutocompleteDropdown
                  labelKey={`${t(`${tRP}.fields.currency`)}`}
                  propsItem={state.donation}
                  field={"currency"}
                  handleChange={handleChangeDonationField}
                  serviceApi={new CurrenciesApiService()}
                  noLabel={true}
                  inputStyle={{
                    padding: "1px 46px 1px 1px",
                    width: "100px",
                  }}
                  fieldProp={"code"}
                />
              }
            />
            {/* <TextField
              label={`${t(`${tRP}.fields.amount`)}`}
              onChange={(e) => handleChangeDonationField("amount", e.target.value)}
              type="number"
              value={state.donation.amount}
              sx={{
                "& .MuiCircularProgress-root": {
                  position: "absolute",
                  marginLeft: "69px",
                },
              }}
              InputProps={{
                endAdornment: (
                  <AutocompleteDropdown
                    labelKey={`${t(`${tRP}.fields.currency`)}`}
                    propsItem={state.donation}
                    field={"currency"}
                    handleChange={handleChangeDonationField}
                    serviceApi={new CurrenciesApiService()}
                    noLabel={true}
                    inputStyle={{
                      padding: "1px 46px 1px 1px",
                      width: "100px",
                    }}
                    fieldProp={"code"}
                    disabled
                  />
                ),
              }}
            /> */}

            <TextField
              label={`${t(`${tRP}.fields.exchange_rate`)}`}
              type="number"
              onChange={(event) => {
                handleChangeDonationField("exchangeRate", event.currentTarget.value);
              }}
              value={state.donation.exchangeRate}
              disabled={isEqual(state.donation?.currency?.id, state.donation?.bookedCurrency?.id)}
              InputProps={{
                endAdornment: (
                  <ExchangeRateBookingsSuggestions exchangeRateSuggestions={state.exchangeRateSuggestions} />
                ),
              }}
            />

            <TextFieldTypeAmount
              field="bookedAmount"
              labelKey={`${t(`${tRP}.fields.converted_amount`)}`}
              handleChange={(_, number) => handleChangeDonationField("bookedAmount", number)}
              amountValue={state.donation.bookedAmount ?? 0}
              sxProps={{
                "& .MuiCircularProgress-root": {
                  position: "absolute",
                  marginLeft: "69px",
                },
              }}
              endAdornment={
                <AutocompleteDropdown
                  labelKey={`${t(`${tRP}.fields.currency`)}`}
                  propsItem={state.donation}
                  field={"bookedCurrency"}
                  handleChange={handleChangeDonationField}
                  serviceApi={new CurrenciesApiService()}
                  noLabel={true}
                  inputStyle={{
                    padding: "1px 46px 1px 1px",
                    width: "100px",
                  }}
                  fieldProp={"code"}
                />
              }
            />
            {/* <TextField
              label={`${t(`${tRP}.fields.converted_amount`)}`}
              type="number"
              value={state.donation.bookedAmount}
              onChange={(e) => handleChangeDonationField("bookedAmount", e.target.value)}
              sx={{
                "& .MuiCircularProgress-root": {
                  position: "absolute",
                  marginLeft: "69px",
                },
              }}
              InputProps={{
                endAdornment: (
                  <>
                    <AutocompleteDropdown
                      labelKey={`${t(`${tRP}.fields.currency`)}`}
                      propsItem={state.donation}
                      field={"bookedCurrency"}
                      handleChange={handleChangeDonationField}
                      serviceApi={new CurrenciesApiService()}
                      noLabel={true}
                      inputStyle={{
                        padding: "1px 46px 1px 1px",
                        width: "100px",
                      }}
                      fieldProp={"code"}
                    />
                  </>
                ),
              }}
            /> */}
          </Grid>
        </DialogContent>
        <DialogActions
          handleCancel={resetAndCloseDialog}
          handleSave={handleConfirm}
          customSaveName={`${t("buttons.confirm")}`}
          disabledSaveButton={!areFieldsValidated()}
        />
      </Dialog>

      <ConfirmationChangesDialog
        open={state.isCCDOpen}
        handleCancel={() => setState({ ...state, isCCDOpen: false })}
        handleDiscard={handleDiscardChanges}
      />
    </>
  );
};

export default AddEditUsagesOfDonationFundsRow;
